import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { ConcentricRings } from '@/components/Motifs';
import api from '@/lib/api';
import { usePageTitle } from '@/hooks/usePageTitle';

export default function EssayDetailPage() {
  const { slug } = useParams();
  const [essay, setEssay] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageTitle(essay?.title);

  useEffect(() => {
    api.get(`/essays/${slug}`).then(res => {
      setEssay(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <SiteHeader />
        <div className="flex justify-center py-32"><ConcentricRings size={60} opacity={0.3} /></div>
      </div>
    );
  }

  if (!essay) {
    return (
      <div className="min-h-screen">
        <SiteHeader />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <h1 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[hsl(var(--foreground))]">Essay not found</h1>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SiteHeader />

      <article className="py-12 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Link
            to="/essays"
            className="inline-flex items-center gap-2 font-['Inter'] text-sm text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))] transition-colors mb-10"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Essays
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Meta */}
            <div className="flex items-center gap-4 mb-6">
              <time className="font-['Inter'] text-xs text-[hsl(var(--silver))]">
                {formatDate(essay.published_at)}
              </time>
              {essay.reading_time && (
                <span className="font-['Inter'] text-xs text-[hsl(var(--muted-foreground))]">
                  {essay.reading_time} min read
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[hsl(var(--foreground))] mb-6 leading-[1.1]">
              {essay.title}
            </h1>

            {/* Excerpt */}
            {essay.excerpt && (
              <p className="font-['Cormorant_Garamond'] text-xl sm:text-2xl italic leading-[1.4] text-[hsl(var(--champagne))] mb-8">
                {essay.excerpt}
              </p>
            )}

            {/* Tags */}
            {essay.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {essay.tags.map(tag => (
                  <Badge
                    key={tag}
                    variant="outline"
                    className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] border-[rgba(168,181,178,0.2)] text-[hsl(var(--silver))] bg-transparent"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            )}

            <Separator className="bg-[rgba(201,168,76,0.15)] mb-10" />

            {/* Body */}
            <div className="reading-room font-['Inter'] text-[15px] sm:text-base leading-[1.8] text-[hsl(var(--muted-foreground))]">
              <ReactMarkdown>{essay.body}</ReactMarkdown>
            </div>

            {/* Substack link */}
            {essay.substack_url && (
              <div className="mt-10 pt-8 border-t border-[rgba(201,168,76,0.1)]">
                <a
                  href={essay.substack_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 font-['Inter'] text-sm text-[hsl(var(--gold))] hover:text-[hsl(var(--champagne))] transition-colors"
                >
                  Read on Substack
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            )}
          </motion.div>
        </div>
      </article>

      <SiteFooter />
    </div>
  );
}
